import React, { useEffect } from 'react';
import dayjs from 'dayjs';
import block from 'bem-cn';

import SVG from 'components/SVG';
import { datePickerStyle } from 'components/InputDate/InputDate';

import useClickOutside from '../model/useClickOutside';
import useDatePicker from '../model/useDatePicker';
import Selector from './Selector';

import calendarImg from './img/calendar.svg';
import arrowImg from './img/arrow.svg';
import chevronImg from './img/chevron.svg';
import triangleImg from './img/arrow-triangle.svg';

import './InputDateCustom.scss';

const b = block('input-date-custom');

const InputDateCustom = ({
  dateFormat = 'DD.MM.YYYY',
  value,
  onChange,
  position,
  children,
  style = datePickerStyle,
  todayWord = 'today',
  deleteWord = 'delete',
  withTime = false,
  startYear,
  yearsCount,
  title = '',
}) => {
  const { ref } = useClickOutside(() => handlers.handleOpen(false));
  const { handlers, values } = useDatePicker({ value, onChange, position, startYear, yearsCount, ref });

  const weekItems = values.weekNames.map(v => (
    <div className={b('cell', { type: 'week' })} key={v}>
      {v}
    </div>
  ));

  const daysItems = values.days.map((v, i) => (
    <div
      className={b('cell', { active: v.active, grey: v.isAnotherMonth })}
      key={`${v.name}_${i}`}
      onClick={() => handlers.handleDaySelect(v)}>
      {v.name}
    </div>
  ));

  const monthItems = values.months.map((v, i) => (
    <div
      className={b('months-elem', { active: v.value === values.bufferValue.get('month') })}
      key={`${v.name}_${i}`}
      onClick={() => handlers.handleMonthSelect(v)}>
      {v.name}
    </div>
  ));

  useEffect(() => {
    const main = ref.current;

    if (style && main) {
      Object.entries(style).forEach(([k, v]) => {
        main.style.setProperty(k, v);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [style]);

  return (
    <div className={b({ open: values.isOpen, yearsOpen: values.isYearsOpen, withTitle: !!title })} ref={ref}>
      {children ? (
        <div className={b('children')} onClick={() => handlers.handleOpen()}>
          {children}
        </div>
      ) : (
        <div className={b('input')} onClick={() => handlers.handleOpen()}>
          <SVG className={b('icon')} svgProps={{ svg: calendarImg }} />
          <div className={b('value')}>
            {title && <div className={b('title')}>{title}</div>}
            {value ? dayjs(value).format(dateFormat) : dateFormat}
          </div>
          <SVG className={b('icon', { type: 'arrow' })} svgProps={{ svg: chevronImg }} />
        </div>
      )}

      {values.isOpen && (
        <div
          className={b('calendar-wrapper', { position: position ?? values.dynamicPosition })}
          ref={values.calendarRef}>
          <div className={b('calendar')}>
            <div className={b('calendar-header')}>
              <div className={b('calendar-date')} onClick={() => handlers.setIsYearsOpen(!values.isYearsOpen)}>
                {dayjs(values.bufferValue).format('MMMM YYYY')}
                <SVG className={b('calendar-triangle')} svgProps={{ svg: triangleImg }} />
              </div>
              {!values.isYearsOpen && (
                <>
                  <SVG
                    className={b('arrow', { type: 'up' })}
                    svgProps={{ svg: arrowImg }}
                    onClick={() => handlers.handleArrowClick(-1, 'month')}
                  />
                  <SVG
                    className={b('arrow')}
                    svgProps={{ svg: arrowImg }}
                    onClick={() => handlers.handleArrowClick(1, 'month')}
                  />
                </>
              )}
            </div>
            {values.isYearsOpen ? (
              <Selector
                className={b('year')}
                items={values.years}
                activeValue={values.bufferValue.get('year')}
                onChange={handlers.handleYearSelect}
                additionalItemsCount={0}
                withScrollOnSelect>
                <div className={b('months')}>{monthItems}</div>
              </Selector>
            ) : (
              <>
                <div className={b('calendar-table')}>
                  {weekItems}
                  {daysItems}
                </div>
                <div className={b('buttons')}>
                  <div className={b('button')} onClick={handlers.handleTodayClick}>
                    {todayWord}
                  </div>
                  {deleteWord && (
                    <div className={b('button')} onClick={handlers.handleDeleteClick}>
                      {deleteWord}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
          {values.isOpen && withTime && (
            <div className={b('time')}>
              <Selector
                className={b('time-element')}
                items={values.hours}
                activeValue={values.bufferValue.get('hour')}
                onChange={handlers.handleHourSelect}
              />
              <Selector
                className={b('time-element')}
                items={values.minutes}
                activeValue={values.bufferValue.get('minute')}
                onChange={handlers.handleMinuteSelect}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default InputDateCustom;
